import { Link } from "@tanstack/react-router";
import { Badge, Card, DataTable, EmptyState, PageHeader, Spinner } from "@empite/ui-components";
import type { Column } from "@empite/ui-components";
import type { Creation } from "../marketplace-client";
import { useMyCreations } from "../marketplace-hooks";
import { ErrorAlert, RequireSession, moderationTone, statusTone, when } from "./common";

/**
 * My creations — including the ones nobody else can see.
 *
 * Both levers are shown side by side because they are genuinely two
 * different facts: `status` is what the author asked for and
 * `moderation_state` is what a moderator decided. A single "live / not live"
 * column would let an author read a moderator's hide as their own draft and
 * try to fix it by republishing, which changes nothing.
 */
export function MyCreations() {
  return <RequireSession>{() => <Mine />}</RequireSession>;
}

const columns: Array<Column<Creation>> = [
  {
    key: "title",
    header: "Title",
    render: (row) => (
      <Link to="/creations/$creationID" params={{ creationID: row.id }}>
        {row.title}
      </Link>
    ),
  },
  {
    key: "status",
    header: "Status",
    render: (row) => <Badge tone={statusTone(row.status)}>{row.status}</Badge>,
  },
  {
    key: "moderation",
    header: "Moderation",
    render: (row) => <Badge tone={moderationTone(row.moderation_state)}>{row.moderation_state}</Badge>,
  },
  { key: "published", header: "Published", render: (row) => when(row.published_at) },
  { key: "likes", header: "Likes", align: "end", render: (row) => row.like_count },
  { key: "comments", header: "Comments", align: "end", render: (row) => row.comment_count },
  { key: "downloads", header: "Downloads", align: "end", render: (row) => row.download_count },
];

function Mine() {
  const mine = useMyCreations();
  const rows = mine.data ?? [];
  const hidden = rows.filter((row) => row.moderation_state !== "visible");

  return (
    <>
      <PageHeader
        title="My creations"
        description="Everything you have uploaded, published or not."
        actions={<Link to="/upload">Upload another</Link>}
      />
      <ErrorAlert error={mine.error} title="We could not list your creations" />
      {mine.isPending ? (
        <Spinner label="Loading your creations" />
      ) : rows.length === 0 ? (
        <EmptyState title="Nothing uploaded yet">
          <p>
            <Link to="/upload">Upload a file</Link> to get started.
          </p>
        </EmptyState>
      ) : (
        <Card>
          {hidden.length > 0 ? (
            // Said plainly rather than hidden: an author who cannot see that
            // a moderator acted has no way to appeal it, and republishing
            // will not clear it.
            <p className="esm-creation__meta">
              {hidden.length} of these were hidden or removed by a moderator. Republishing does not
              undo that.
            </p>
          ) : null}
          <DataTable columns={columns} rows={rows} rowKey={(row) => row.id} empty="Nothing yet." />
        </Card>
      )}
    </>
  );
}
